import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { VoteService } from '../../shared/services/vote.service';
import { ProjectService } from '../../shared/services/project.service';
import { OverlayService } from '../../shared/services/overlayService';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { SubsectionService } from '../../shared/services/subsection.service';
import { BatchVote, OptionInfo, OptionVersion, Project, User, UserVote } from '../../shared/interfaces';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../shared/services/auth.service';

@Component({
  selector: 'assets-approval',
  templateUrl: './assets-approval.component.html',
  styleUrls: ['./assets-approval.component.scss'],
  standalone: false,
})
export class AssetsApprovalComponent implements OnInit, OnDestroy {
  @Input() defaultVote: string = 'YES';
  @Input() compareMode: boolean = false;
  @Input() activeOption: OptionInfo;
  @Input() versionData: OptionVersion;

  private activeModal = inject(NgbActiveModal);
  private voteService = inject(VoteService);
  private projectService = inject(ProjectService);
  private overlayService = inject(OverlayService);
  private translatePipe = inject(TranslatePipe);
  private subsectionService = inject(SubsectionService);
  private authService = inject(AuthService);

  isLoading: boolean = false;
  voteOptions: any[] = [
    { value: 'YES', text: 'accept' },
    { value: 'NO', text: 'versioning_reject' },
    { value: 'NA', text: 'versioning_batchApproval_resetSelection' },
  ];
  selectedAction: string = 'YES';
  selectedLabel: string = 'accept';
  isSelectAll: boolean = false;
  selectedRecord: Record<string, boolean> = {};
  approvalVotes: Record<string, UserVote[]> = {};
  rejectionVotes: Record<string, UserVote[]> = {};
  private ngUnsubscribe = new Subject<void>();

  ngOnInit() {
    this.changeAction(this.defaultVote);
    if (this.compareMode) {
      this.populateRecord();
    } else {
      this.subsectionService.subsection$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
        next: (value) => {
          if (value.versionData) {
            this.activeOption = value.activeOption;
            this.versionData = value.versionData;
            this.populateRecord();
          }
        },
      });
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  changeAction(value: string) {
    this.selectedAction = value;
    const action = this.voteOptions.find((option) => option.value === value);
    if (action) {
      this.selectedLabel = action.text;
    }
  }

  toggleSelectAll() {
    this.isSelectAll = !this.isSelectAll;
    Object.keys(this.selectedRecord).forEach((key) => {
      this.selectedRecord[key] = this.isSelectAll;
    });
  }

  public onSelectAllChange(checked: boolean): void {
    if (this.isPartialSelection()) {
      this.isSelectAll = true;
      Object.keys(this.selectedRecord).forEach((key) => {
        this.selectedRecord[key] = true;
      });
    } else {
      this.isSelectAll = checked;
      Object.keys(this.selectedRecord).forEach((key) => {
        this.selectedRecord[key] = checked;
      });
    }
  }

  public adjustCheckState(): void {
    this.isSelectAll = Object.values(this.selectedRecord).every((value) => value);
  }

  public isPartialSelection(): boolean {
    const selectedCount = Object.values(this.selectedRecord).filter((value) => value).length;
    const totalCount = Object.keys(this.selectedRecord).length;
    return selectedCount > 0 && selectedCount < totalCount;
  }

  cancel() {
    this.activeModal.dismiss();
  }

  save() {
    if (this.isLoading) {
      return;
    }

    const votes: Record<string, string> = {};
    Object.keys(this.selectedRecord).forEach((key) => {
      if (this.selectedRecord[key]) {
        votes[key] = this.selectedAction;
      }
    });

    if (Object.entries(votes).length === 0) {
      this.overlayService.showError(this.translatePipe.transform('edit_vote_noSelection'));
      return;
    }

    const dto: BatchVote = {
      projectId: this.projectService.project.id,
      sectionId: this.activeOption.section,
      subsectionId: this.activeOption.subsection,
      optionId: this.activeOption.optionId,
      votes: votes,
      version: this.versionData.versionNumber,
    };
    this.isLoading = true;
    this.voteService.sendBatchVote(dto).subscribe({
      next: (value) => {
        this.isLoading = false;
        this.overlayService.showSuccess(this.translatePipe.transform('overlayChangeSave'));
        this.syncVersionData(votes);
        this.activeModal.close(true);
      },
      error: (error) => {
        this.isLoading = false;
        this.overlayService.showError(this.translatePipe.transform('edit_vote_voteFailed'));
      },
    });
  }

  private populateRecord() {
    this.selectedRecord = {};
    this.versionData.files.forEach((file) => {
      this.selectedRecord[file.id] = false;
    });
    this.approvalVotes = {};
    this.rejectionVotes = {};

    if (this.versionData.votesBatch) {
      Object.entries(this.versionData.votesBatch).forEach(([fileId, votes]) => {
        const approvalVotes = votes.filter((vote) => vote.vote === 'YES');
        const rejectionVotes = votes.filter((vote) => vote.vote === 'NO');
        this.approvalVotes[fileId] = approvalVotes;
        this.rejectionVotes[fileId] = rejectionVotes;
      });
    }
  }

  private syncVersionData(votes: Record<string, string>) {
    const username = this.authService.getAuthUserName();
    const participant = (this.projectService.project as Project)?.participants?.find((p) => p.username == username);

    if (!participant) {
      return;
    }

    this.versionData.files.forEach((file) => {
      if (file.id in votes) {
        if (!this.versionData.votesBatch) {
          this.versionData.votesBatch = {};
        }

        if (!this.versionData.votesBatch[file.id]) {
          this.versionData.votesBatch[file.id] = [];
        }

        const vote = this.versionData?.votesBatch[file.id].find((u) => u.username == username);
        if (vote) {
          vote.vote = votes[file.id];
        } else {
          this.versionData.votesBatch[file.id].push({
            username: username,
            fullName: participant.fullName,
            avatarUrl: participant.avatarUrl,
            blocked: participant.blocked,
            votedAt: Date.now(),
            vote: votes[file.id],
          });
        }
      }
    });
  }
}
